import { gql, useLazyQuery } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { type EventsType, useIdleTimer } from 'react-idle-timer';
import InactiveLayoutModal from '../ovComponents/3-pattern/inActivityLayout/inactivityLayout';
import { useAuthContext } from './ovApolloProvider';
import { UserContext } from './userContextProvider';

const SILENT_REFRESH = gql`
  query refreshCustodianAuthToken($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        custodianStatistics {
          marketValueCents
        }
      }
    }
  }
`;

export const LAST_ACTIVITY_TIME_STAMP = 'lastActivityTimestamp';

type InactivityTimeoutContextType = {
  attemptSilentRefresh?: () => void;
};

const InactivityTimeoutContext = createContext<InactivityTimeoutContextType>(
  {},
);

export const useInactivityTimeout = () => useContext(InactivityTimeoutContext);

const InactivityTimeoutProvider = ({ children }: { children: any }) => {
  const { appLogout, settings } = useAuthContext();
  const { activeEntity } = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const [refresh] = useLazyQuery(SILENT_REFRESH, {
    fetchPolicy: 'network-only',
  });

  const enableInactivityTimeOut = settings?.enableInactivityTimeOut || false;
  const isHostedLogin =
    settings?.useHostedLogin ||
    (settings?.clientHostedLoginRedirectUri && settings?.clientHostedLoginURL);

  const inactivityTimeoutInMilliseconds = useMemo(
    () => (settings?.inactivityTimeoutInMinutes || 15) * 60000,
    // when testing or debugging inactivity timeout related tasks,
    // use the below line to wait for very little time (1/6 of the time)
    // () => ((settings?.inactivityTimeoutInMinutes || 15) / 7) * 60000,
    [settings?.inactivityTimeoutInMinutes],
  );

  const events: EventsType[] = [
    'mousemove',
    'keydown',
    'keyup',
    'click',
    'DOMMouseScroll',
    'mousewheel',
    'mousedown',
    'touchstart',
    'touchmove',
    'scroll',
    'focus',
  ];

  type SilentRefreshCallback = {
    success?: (data?: any) => void;
    failure?: (error?: unknown) => void;
  };

  const silentRefresh = (callBack?: SilentRefreshCallback) => {
    if (isHostedLogin && activeEntity?.id) {
      const refreshPromise = refresh({
        variables: { userId: activeEntity?.id },
      });
      if (callBack) {
        refreshPromise
          .then(({ data }) => {
            callBack.success?.(data);
          })
          .catch((error) => {
            callBack.failure?.(error);
          });
      }
    } else {
    }
    // reloads screen when [silentRefresh] is called
    // window.location.reload();
  };

  const logout = async ({
    reason,
    preventSingleLogout,
  }: {
    reason?: string;
    preventSingleLogout?: boolean;
  } = {}) => {
    const lastActivityTimestamp = localStorage.getItem(
      LAST_ACTIVITY_TIME_STAMP,
    );

    datadogLogs.logger.warn(
      'InactivityTimeoutProvider - Inactivity session timeout, logging user out...',
      {
        lastActivityTimestamp,
        userId: activeEntity?.id,
        reason,
        preventSingleLogout,
      },
    );

    localStorage.removeItem(LAST_ACTIVITY_TIME_STAMP);

    await appLogout({ reason, preventSingleLogout });
  };

  // Logs user out due to inactivity preventing single logout
  const logoutDueToInactivity = async () => {
    await logout({ reason: 'inactive', preventSingleLogout: true });
  };

  // Handle logout specifically from the modal button click
  // This closes the modal immediately to provide instant feedback,
  // then performs the logout. This prevents the countdown timer from
  // continuing to run during the async logout process.
  // Because the user actively clicked the logout button, we don't want to prevent single logout.
  const handleLogoutFromModal = async () => {
    setOpen(false);
    await logout();
  };

  const stayLoggedIn = async () => {
    setOpen(false);
    reset();
    silentRefresh();
    localStorage.setItem(LAST_ACTIVITY_TIME_STAMP, new Date().toISOString());
  };

  const checkSessionAndHandleTimeout = async () => {
    const lastActivityTimestamp = localStorage.getItem(
      LAST_ACTIVITY_TIME_STAMP,
    );

    if (!lastActivityTimestamp) {
      return;
    }

    const lastActivityDate = new Date(lastActivityTimestamp);
    const today = new Date();
    const timeDiff = today.getTime() - lastActivityDate.getTime();

    if (timeDiff >= inactivityTimeoutInMilliseconds) {
      // Before logging out, check if this is a hosted login session
      if (isHostedLogin) {
        const clientHostedLoginToken = window.localStorage.getItem(
          'clientHostedLoginToken',
        );

        if (clientHostedLoginToken) {
          // Try to validate the session with the identity provider
          silentRefresh({
            success: (data) => {
              if (
                data?.fetchUser?.user?.custodianStatistics?.marketValueCents
              ) {
                localStorage.setItem(
                  LAST_ACTIVITY_TIME_STAMP,
                  new Date().toISOString(),
                );
              } else {
                logoutDueToInactivity();
              }
            },
            failure: () => {
              logoutDueToInactivity();
            },
          });
          return;
        }
      }
      // Only logout if session is invalid or not hosted login

      logout();
    } else {
      silentRefresh();
    }
  };

  const { reset, getRemainingTime } = useIdleTimer({
    timeout: inactivityTimeoutInMilliseconds,
    promptBeforeIdle: inactivityTimeoutInMilliseconds / 10,
    events,
    disabled: !enableInactivityTimeOut,
    crossTab: true,
    throttle: inactivityTimeoutInMilliseconds / 4,
    onAction: () => {
      localStorage.setItem(LAST_ACTIVITY_TIME_STAMP, new Date().toISOString());
      silentRefresh();
    },
    onIdle: () => logoutDueToInactivity(),
    onPrompt: async () => {
      if (getRemainingTime() <= 0) {
        await logoutDueToInactivity();
        return;
      }
      silentRefresh();
      setOpen(true);
    },
  });

  useEffect(() => {
    const lastActivityTimestamp = localStorage.getItem(
      LAST_ACTIVITY_TIME_STAMP,
    );
    if (!lastActivityTimestamp)
      localStorage.setItem(LAST_ACTIVITY_TIME_STAMP, new Date().toISOString());
  }, []);

  useEffect(() => {
    if (!enableInactivityTimeOut) return;

    document.addEventListener('visibilitychange', async () => {
      if (enableInactivityTimeOut && getRemainingTime() <= 0) {
        // Use the same session validation logic
        const clientHostedLoginToken = window.localStorage.getItem(
          'clientHostedLoginToken',
        );
        if (isHostedLogin && clientHostedLoginToken) {
          checkSessionAndHandleTimeout();
          return;
        }
        await logout();
        return;
      }
      silentRefresh();
    });
  }, []);

  useEffect(() => {
    if (!enableInactivityTimeOut) return;

    checkSessionAndHandleTimeout();
  }, [inactivityTimeoutInMilliseconds]);

  return (
    <InactivityTimeoutContext.Provider
      value={{ attemptSilentRefresh: silentRefresh }}
    >
      {children}
      <InactiveLayoutModal
        open={open}
        onLogout={handleLogoutFromModal}
        stayLoggedIn={stayLoggedIn}
        onSessionTimeOut={logoutDueToInactivity}
      />
    </InactivityTimeoutContext.Provider>
  );
};

export default InactivityTimeoutProvider;
